import { createContext, useContext, useState, useEffect } from 'react';

const THEME_PALETTES = {
  amber: {
    name: 'Amber',
    primary: 'amber',
    colors: {
      50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d',
      400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309',
      800: '#92400e', 900: '#78350f'
    }
  },
  teal: {
    name: 'Teal',
    primary: 'teal',
    colors: {
      50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4',
      400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e',
      800: '#115e59', 900: '#134e4a'
    }
  },
  blue: {
    name: 'Blue',
    primary: 'blue',
    colors: {
      50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd',
      400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8',
      800: '#1e40af', 900: '#1e3a8a'
    }
  },
  purple: {
    name: 'Purple',
    primary: 'purple',
    colors: {
      50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe',
      400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7e22ce',
      800: '#6b21a8', 900: '#581c87'
    }
  },
  green: {
    name: 'Green',
    primary: 'green',
    colors: {
      50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac',
      400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d',
      800: '#166534', 900: '#14532d'
    }
  },
  orange: {
    name: 'Orange',
    primary: 'orange',
    colors: {
      50: '#fff7ed', 100: '#ffedd5', 200: '#fed7aa', 300: '#fdba74',
      400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c',
      800: '#9a3412', 900: '#7c2d12'
    }
  },
  pink: {
    name: 'Pink',
    primary: 'pink',
    colors: {
      50: '#fdf2f8', 100: '#fce7f3', 200: '#fbcfe8', 300: '#f9a8d4',
      400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d',
      800: '#9d174d', 900: '#831843'
    }
  }
};

const PortalThemeContext = createContext();

export const usePortalTheme = () => {
  const context = useContext(PortalThemeContext);
  if (!context) {
    throw new Error('usePortalTheme must be used within PortalThemeProvider');
  }
  return context;
};

export const PortalThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('portalTheme');
    return saved || 'amber';
  });

  useEffect(() => {
    localStorage.setItem('portalTheme', currentTheme);
  }, [currentTheme]);

  const theme = THEME_PALETTES[currentTheme] || THEME_PALETTES.amber;

  // Generate CSS class helpers
  const getThemeClasses = (type) => {
    const t = currentTheme;
    switch (type) {
      case 'sidebar':
        return `bg-gradient-to-b from-${t}-700 to-${t}-900`;
      case 'sidebarBorder':
        return `border-${t}-600`;
      case 'sidebarText':
        return `text-${t}-200`;
      case 'sidebarTextHover':
        return `hover:bg-white/10 hover:text-white`;
      case 'sidebarActive':
        return 'bg-white/20 text-white';
      case 'primary':
        return `bg-${t}-600 hover:bg-${t}-700`;
      case 'primaryText':
        return `text-${t}-600`;
      case 'primaryBorder':
        return `border-${t}-300`;
      case 'primaryBg':
        return `bg-${t}-50`;
      case 'primaryBgHover':
        return `hover:bg-${t}-50`;
      case 'card':
        return `bg-gradient-to-br from-${t}-50 to-${t}-100 border-${t}-200`;
      case 'cardText':
        return `text-${t}-700`;
      case 'cardTextBold':
        return `text-${t}-800`;
      case 'iconBg':
        return `bg-${t}-500/20`;
      case 'icon':
        return `text-${t}-600`;
      case 'badge':
        return `bg-${t}-100 text-${t}-800`;
      case 'spinner':
        return `border-${t}-600`;
      case 'mobileHeader':
        return `text-${t}-600`;
      default:
        return '';
    }
  };

  const value = {
    currentTheme,
    setCurrentTheme,
    theme,
    palettes: THEME_PALETTES,
    getThemeClasses
  };

  return (
    <PortalThemeContext.Provider value={value}>
      {children}
    </PortalThemeContext.Provider>
  );
};

export default PortalThemeContext;
